import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericModal from "../components/GenericModal";
import Loader from "../../components/loader/Loader";
import { MAX_SECTION_NAME, NAME_PATTERN } from "./helpManualData";
import HelpManualSelect from "./HelpManualSelect";
import {
  addOrEditHelpManualSection,
  getHelpManualSectionDropdown,
  getSectionAndTopicMaster,
  mapSectionAndTopicMaster,
  mapSectionDropdownOptions,
  resolvePlaceAfterSectionId,
} from "./helpManualApi";

const SectionForm = () => {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const isEdit = Boolean(sectionId);
  const numericSectionId = isEdit ? Number(sectionId) : 0;

  const [name, setName] = useState("");
  const [placeAfter, setPlaceAfter] = useState("");
  const [placeOptions, setPlaceOptions] = useState([
    { value: "0", label: "Start" },
  ]);
  const [nameError, setNameError] = useState("");
  const [placeAfterError, setPlaceAfterError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [infoModal, setInfoModal] = useState({
    open: false,
    title: "",
    description: "",
    type: "success",
  });
  const savingRef = useRef(false);
  const formLoadedRef = useRef(false);

  const goToList = () => navigate("/helpmanual");

  const showError = (message) => {
    setInfoModal({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  const loadForm = async () => {
    if (isEdit && (!numericSectionId || Number.isNaN(numericSectionId))) {
      goToList();
      return;
    }

    setIsLoading(true);
    try {
      const dropdownPromise = getHelpManualSectionDropdown(
        isEdit && numericSectionId > 0 ? numericSectionId : null
      );

      if (isEdit) {
        const [dropdownData, tocData] = await Promise.all([
          dropdownPromise,
          getSectionAndTopicMaster(),
        ]);

        const sections = mapSectionAndTopicMaster(tocData);
        const section = sections.find(
          (s) => Number(s.id) === numericSectionId
        );
        if (!section) {
          showError("Section was not found or is inactive.");
          return;
        }

        const options = mapSectionDropdownOptions(dropdownData);
        setPlaceOptions(
          options.length > 0 ? options : [{ value: "0", label: "Start" }]
        );

        const resolvedPlaceAfter = resolvePlaceAfterSectionId(
          sections,
          numericSectionId
        );
        setName(section.name || "");
        setPlaceAfter(
          options.some((o) => o.value === resolvedPlaceAfter)
            ? resolvedPlaceAfter
            : "0"
        );
      } else {
        const dropdownData = await dropdownPromise;
        const options = mapSectionDropdownOptions(dropdownData);
        setPlaceOptions(
          options.length > 0 ? options : [{ value: "0", label: "Start" }]
        );
        setPlaceAfter("");
        setName("");
      }

      setNameError("");
      setPlaceAfterError("");
    } catch (err) {
      console.error("SectionForm load error:", err);
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        showError(
          err.response?.data?.message ||
            err.response?.data?.Message ||
            "Failed to load section form."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formLoadedRef.current) return;
    formLoadedRef.current = true;
    loadForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = () => {
    const trimmed = name.trim();
    let isValid = true;

    if (!trimmed) {
      setNameError("Section name is required.");
      isValid = false;
    } else if (trimmed.length > MAX_SECTION_NAME) {
      setNameError(`Section name must be at most ${MAX_SECTION_NAME} characters.`);
      isValid = false;
    } else if (!NAME_PATTERN.test(trimmed)) {
      setNameError("Special characters are not allowed.");
      isValid = false;
    } else {
      setNameError("");
    }

    if (placeAfter === "" || placeAfter === null || placeAfter === undefined) {
      setPlaceAfterError("Place after section is required.");
      isValid = false;
    } else {
      setPlaceAfterError("");
    }

    return isValid;
  };

  const requestSave = () => {
    if (savingRef.current || isLoading) return;
    if (!validate()) return;
    setShowSaveModal(true);
  };

  const confirmSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setShowSaveModal(false);
    setIsLoading(true);

    try {
      const result = await addOrEditHelpManualSection({
        id: isEdit ? numericSectionId : 0,
        name: name.trim(),
        placeAfterSectionId: Number(placeAfter),
      });

      const statusCode = result.statusCode ?? result.StatusCode;
      const message = result.message ?? result.Message;

      if (statusCode === 1) {
        setInfoModal({
          open: true,
          title: isEdit ? "Section updated" : "Section saved",
          description:
            message ||
            `"${name.trim()}" has been ${
              isEdit ? "updated" : "added to the help manual"
            }.`,
          type: "success",
        });
      } else {
        setNameError(message || "Failed to save section.");
        showError(message || "Failed to save section.");
      }
    } catch (err) {
      console.error("AddOrEditHelpManualSection error:", err);
      if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "Failed to save section. Please try again.";
        setNameError(errorMsg);
        showError(errorMsg);
      }
    } finally {
      setIsLoading(false);
      savingRef.current = false;
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="bg-white rounded-lg flex flex-col h-full min-h-0 overflow-hidden">
        <div className="px-6 pt-6 pb-4 shrink-0">
          <h1 className="text-xl font-semibold">
            {isEdit ? "Edit Section" : "Add Section"}
          </h1>
          <nav className="text-sm mt-1" aria-label="Breadcrumb">
            <span
              className="text-blue-500 cursor-pointer hover:underline"
              onClick={goToList}
            >
              Help Manual
            </span>
            <span className="mx-1 text-gray-400">&gt;</span>
            <span className="text-primary">
              {isEdit ? "Edit Section" : "Add Section"}
            </span>
          </nav>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 pb-4">
          <div className="max-w-xl space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Section Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError("");
                }}
                maxLength={MAX_SECTION_NAME}
                placeholder="Enter section name"
                className={`w-full border rounded-md px-3 py-2 text-sm input-field ${
                  nameError
                    ? "input-error"
                    : ""
                }`}
              />
              <div className="min-h-[16px]">
                {nameError && (
                  <p className="text-xs text-red-500 mt-1">{nameError}</p>
                )}
              </div>
            </div>

            <HelpManualSelect
              label="Place after Section"
              required
              value={placeAfter}
              options={placeOptions}
              error={placeAfterError}
              onChange={(value) => {
                setPlaceAfter(value);
                setPlaceAfterError("");
              }}
            />
          </div>
        </div>

        <div className="help-manual-form-actions">
          <button
            type="button"
            className="w-[120px]"
            onClick={() => setShowCancelModal(true)}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-save w-[120px]"
            onClick={requestSave}
            disabled={isLoading}
          >
            {isEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>

      <GenericModal
        open={showCancelModal}
        title="Confirm Cancel"
        description="Are you sure to cancel and exit?"
        primaryText="Yes"
        secondaryText="No"
        onPrimary={goToList}
        onClose={() => setShowCancelModal(false)}
      />

      <GenericModal
        open={showSaveModal}
        title={isEdit ? "Update Section" : "Save Section"}
        description={`Are you sure you want to ${
          isEdit ? "update" : "save"
        } this section?`}
        primaryText={isEdit ? "Update" : "Save"}
        secondaryText="Cancel"
        onPrimary={confirmSave}
        onClose={() => setShowSaveModal(false)}
      />

      <GenericModal
        open={infoModal.open}
        title={infoModal.title}
        description={infoModal.description}
        primaryText="OK"
        type={infoModal.type}
        onClose={
          infoModal.type === "success"
            ? goToList
            : () => setInfoModal((prev) => ({ ...prev, open: false }))
        }
        onPrimary={
          infoModal.type === "success"
            ? goToList
            : () => setInfoModal((prev) => ({ ...prev, open: false }))
        }
      />
    </>
  );
};

export default SectionForm;
